import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cookie from 'js-cookie';
import queryString from 'query-string';

import { getIsDisabledPerformanceCookies } from 'helpers/cookiesSettings';
import { setLocalStorageItem } from 'helpers/localStorage';
import { isPWAMode } from 'helpers/pwa/utils';
import { isMonetizationPage, isTermsOrPolicyPage } from 'helpers/route';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';
import { assertString } from 'helpers/utils/checkType';
import { getIsBuyer } from 'store/auth/selectors';
import { isCanShowRetentionPopupSelector } from 'store/common/selectors';

import {
  EMAIL_CONFIRMATION_PARTNER_LOGIN_ID,
  TRANSFER_POPUP_STORAGE_KEY,
} from '../constants';
import { useCurrentTheme } from './useCurrentTheme';
import { useTransferPopupAnalytics } from './useTransferPopupAnalytics';
import { useUserTransferLinkQuery } from './useUserTransferLinkQuery';

export const useTransferUserPopup = () => {
  const location = useLocation();
  const isUserBuyer = useSelector(getIsBuyer);
  const isCanShowRetentionPopup = useSelector(isCanShowRetentionPopupSelector);
  const queries = queryString.parse(location.search);
  const isRetention = queries.partner_login_id;
  const isConfirmEmailRetention =
    queries.partner_login_id === EMAIL_CONFIRMATION_PARTNER_LOGIN_ID;
  const isPopupCanShow = Boolean(
    isUserBuyer &&
      isCanShowRetentionPopup &&
      !cookie.get(TRANSFER_POPUP_STORAGE_KEY) &&
      !getSessionStorageItem(TRANSFER_POPUP_STORAGE_KEY) &&
      isRetention &&
      !isConfirmEmailRetention &&
      !isPWAMode()
  );
  const userTransferLinkQuery = useUserTransferLinkQuery(isPopupCanShow);
  const currentTheme = useCurrentTheme();
  const { trackPopupShown, trackPopupClick, trackPopupClose } =
    useTransferPopupAnalytics(currentTheme);

  const [isVisible, setIsVisible] = useState(false);

  const handleMount = () => {
    if (userTransferLinkQuery.isSuccess) {
      const { query } = queryString.parseUrl(userTransferLinkQuery.data.url);
      trackPopupShown(assertString(query.ad_id) ? query.ad_id : null);
    }

    if (!getIsDisabledPerformanceCookies()) {
      cookie.set(TRANSFER_POPUP_STORAGE_KEY, String(currentTheme), {
        expires: 0.5, // 12 hours
        sameSite: 'None',
        secure: true,
      });
    }

    setSessionStorageItem(TRANSFER_POPUP_STORAGE_KEY, String(currentTheme));
    setLocalStorageItem(TRANSFER_POPUP_STORAGE_KEY, String(currentTheme));
  };

  const handleClose = () => {
    trackPopupClose();
    setIsVisible(false);
  };

  const handleSubmit = () => {
    trackPopupClick();
    setIsVisible(false);
    window.open(userTransferLinkQuery.data?.url, '_blank');
  };

  useEffect(() => {
    if (
      userTransferLinkQuery.isSuccess &&
      !isMonetizationPage(location.pathname) &&
      !isTermsOrPolicyPage(location.pathname) &&
      !cookie.get(TRANSFER_POPUP_STORAGE_KEY)
    ) {
      setIsVisible(true);
    }
  }, [userTransferLinkQuery.isSuccess, location.pathname]);

  return {
    isVisible,
    theme: currentTheme,
    handleMount,
    handleSubmit,
    handleClose,
  };
};
